import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { gossipArticles, morningArticles } from "@/mocks/articles";
import { hotTags, siteInfo } from "@/mocks/site";

const BASE = "https://mynami.cn";

export default function Morning() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "每日早报",
      url: `${BASE}/morning`,
      description:
        "每日早报汇总当日娱乐行业动态，两分钟读完值得关注的资讯，固定栏目准时送达。",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: morningArticles.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${BASE}/article/${item.id}`,
          name: item.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "每日早报", item: `${BASE}/morning` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="每日早报 | 黑料每日两分钟行业动态速览"
        description="每日早报每天固定更新，汇总当日娱乐行业动态，涵盖短剧出海、音乐节规则、综艺招商、影视基地与平台分账等话题。"
        keywords="黑料每日早报,每日早报,娱乐行业动态,行业速览,每日精选"
        canonicalPath="/morning"
        jsonLd={jsonLd}
      />
      <div className="px-4 md:px-8">
        <Breadcrumbs items={[{ label: "每日早报" }]} />
      </div>

      <section className="px-4 md:px-8 pb-6 border-b border-background-200">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.2em] text-accent-700 uppercase mb-2">
          <span className="w-6 h-px bg-accent-500" />
          MORNING BRIEF
        </span>
        <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-3">每日早报</h1>
        <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          把过去一整天散落在各处的行业信息收进一份清单，两分钟读完，知道发生了什么、值得关注什么。
        </p>
        <p className="mt-3 text-xs text-foreground-500 flex items-center gap-1.5">
          <i className="ri-refresh-line" aria-hidden="true" />
          {siteInfo.updateTime}
        </p>
      </section>

      <div className="px-4 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
        <section className="lg:col-span-2" aria-label="早报列表">
          <div className="flex flex-col divide-y divide-background-200 border-y border-background-200">
            {morningArticles.map((item, index) => (
              <article key={item.id} className="py-5 first:pt-0">
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 font-heading text-base font-bold">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1.5">
                      <span className="text-[11px] font-medium text-primary-600">{item.category}</span>
                      <time className="text-[11px] text-foreground-400" dateTime={item.date}>
                        {item.date}
                      </time>
                    </div>
                    <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug mb-2">
                      <Link to={`/article/${item.id}`} className="hover:text-primary-600 transition-colors">
                        {item.title}
                      </Link>
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.excerpt}</p>
                    <Link
                      to={`/article/${item.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      阅读全文
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <aside className="lg:col-span-1 flex flex-col gap-8" aria-label="侧栏">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-flashlight-line text-primary-500" aria-hidden="true" />
              一瓜推荐
            </h2>
            <div>
              {gossipArticles.slice(0, 3).map((item) => (
                <ArticleCard key={item.id} article={item} variant="compact" />
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-hashtag text-primary-500" aria-hidden="true" />
              热门标签
            </h2>
            <div className="flex flex-wrap gap-2">
              {hotTags.slice(0, 8).map((tag) => (
                <Link
                  key={tag}
                  to={`/gossip?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full border border-background-300 text-xs text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}